import { NOTE_CUT, TrackerSong, TrackerNote, MAX_PLAY_RATE, effectById, finalizeSound, LIMITS, playableNote } from "./tracker";

// writes an impulse tracker module (.it) from a song. written from the ITTECH spec, kept to what pvsneslib's smconv
// needs: instrument mode with one sample per instrument, samples stored raw (never the compressed form), at most 8
// voices in the patterns, no panning or envelopes, note cut as the only new note action.
//
// layout, in file order:
//   header (192 bytes) | order list | instrument, sample and pattern offset tables
//   | instruments (554 bytes each) | sample headers (80 each) | patterns | sample data
// every number is little endian

const HEADER = 192;
const INSTRUMENT_SIZE = 554;
const SAMPLE_HEADER = 80;
const NOTE_CUT_IT = 254;
const ORDER_END = 255;
const IT_NAME = 26;
const MAX_CHANNELS = 64;

class Bytes {
  private data: number[] = [];
  get length(): number {
    return this.data.length;
  }
  u8(value: number): void {
    this.data.push(value & 255);
  }
  u16(value: number): void {
    this.u8(value);
    this.u8(value >> 8);
  }
  u32(value: number): void {
    this.u16(value);
    this.u16(value >>> 16);
  }
  text(value: string, size: number): void {
    for (let i = 0; i < size; i++) this.u8(i < value.length ? value.charCodeAt(i) & 127 : 0);
  }
  zeros(count: number): void {
    for (let i = 0; i < count; i++) this.u8(0);
  }
  raw(bytes: ArrayLike<number>): void {
    for (let i = 0; i < bytes.length; i++) this.u8(bytes[i]);
  }
  out(): Uint8Array {
    return Uint8Array.from(this.data);
  }
}

export interface ItReport {
  channels: number;
  samples: number;
  instruments: number;
  patterns: number;
  // samples stored, in bytes of 8 bit audio, before brr
  sampleBytes: number;
  // notes that were lowered to stay under the 128 khz limit
  loweredNotes: number;
}

export interface ItResult {
  bytes: Uint8Array;
  report: ItReport;
}

function safeName(text: string): string {
  return text.replace(/[^\x20-\x7e]/g, "?").slice(0, IT_NAME - 1);
}

function dosName(text: string): string {
  return text.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
}

function packPattern(song: TrackerSong, notes: TrackerNote[], rows: number): Uint8Array {
  const out = new Bytes();
  for (let row = 0; row < rows; row++) {
    for (const note of notes.filter((item) => item.r === row)) {
      let mask = 1;
      const cut = note.n === NOTE_CUT;
      if (!cut) mask |= 2;
      if (!cut && note.v >= 0) mask |= 4;
      const effect = effectById(note.fx);
      if (effect) mask |= 8;
      out.u8((note.c + 1) | 128);
      out.u8(mask);
      out.u8(cut ? NOTE_CUT_IT : note.n);
      if (mask & 2) out.u8(note.s + 1);
      if (mask & 4) out.u8(Math.min(64, note.v));
      if (effect) {
        out.u8(effect.it);
        out.u8(note.fp ?? effect.start);
      }
    }
    out.u8(0);
  }
  return out.out();
}

export function writeIt(song: TrackerSong): ItResult {
  const limit = LIMITS[song.mode];
  const finals = song.sounds.map(finalizeSound);
  let loweredNotes = 0;
  const patternNotes = song.patterns.map((pattern) => pattern.notes.filter((note) => note.c < limit.voices).map((note) => {
    const fixed = playableNote(song, note);
    if (fixed.n !== note.n) loweredNotes++;
    if (note.n !== NOTE_CUT && !song.sounds[note.s]) throw new Error("a note has no sound, fix that before exporting");
    return fixed;
  }));
  const channels = Math.max(1, Math.min(MAX_CHANNELS, patternNotes.reduce((most, notes) => notes.reduce((high, note) => Math.max(high, note.c + 1), most), 0)));
  const orders = song.order.length + 1;
  const insCount = song.sounds.length;
  const patCount = song.patterns.length;

  const patterns = song.patterns.map((pattern, index) => packPattern(song, patternNotes[index], pattern.rows));
  const tables = HEADER + orders;
  const insAt = tables + (insCount + insCount + patCount) * 4;
  const smpAt = insAt + insCount * INSTRUMENT_SIZE;
  const patAt = smpAt + insCount * SAMPLE_HEADER;
  let cursor = patAt;
  const patternAt = patterns.map((bytes) => {
    const at = cursor;
    cursor += 8 + bytes.length;
    return at;
  });
  const dataAt = finals.map((sound) => {
    const at = cursor;
    cursor += sound.data.length;
    return at;
  });

  const out = new Bytes();
  out.text("IMPM", 4);
  out.text(safeName(song.title), IT_NAME);
  out.u8(4);
  out.u8(16);
  out.u16(orders);
  out.u16(insCount);
  out.u16(insCount);
  out.u16(patCount);
  // made with 2.14, needs 2.14. flags: stereo, use instruments, linear slides
  out.u16(0x0214);
  out.u16(0x0214);
  out.u16(1 | 4 | 8);
  out.u16(0);
  out.u8(128);
  out.u8(48);
  out.u8(song.speed);
  out.u8(song.tempo);
  out.u8(128);
  out.u8(0);
  out.u16(0);
  out.u32(0);
  out.u32(0);
  // panning: centre on the voices in use, the rest switched off
  for (let i = 0; i < 64; i++) out.u8(i < channels ? 32 : 32 + 128);
  for (let i = 0; i < 64; i++) out.u8(64);
  for (const value of song.order) out.u8(value);
  out.u8(ORDER_END);
  for (let i = 0; i < insCount; i++) out.u32(insAt + i * INSTRUMENT_SIZE);
  for (let i = 0; i < insCount; i++) out.u32(smpAt + i * SAMPLE_HEADER);
  for (let i = 0; i < patCount; i++) out.u32(patternAt[i]);

  // one instrument per sound, every key plays that sound, no envelopes
  song.sounds.forEach((sound, index) => {
    out.text("IMPI", 4);
    out.text(dosName(sound.name), 12);
    out.u8(0);
    // new note action: cut, duplicate check: off
    out.u8(0);
    out.u8(0);
    out.u8(0);
    out.u16(0);
    out.u8(0);
    out.u8(60);
    out.u8(128);
    // default pan 32, bit 7 set means "do not use"
    out.u8(32 + 128);
    out.u8(0);
    out.u8(0);
    out.u16(0);
    out.u8(1);
    out.u8(0);
    out.text(safeName(sound.name), IT_NAME);
    out.u8(0);
    out.u8(0);
    out.u8(0);
    out.u8(0);
    out.u16(0);
    for (let note = 0; note < 120; note++) {
      out.u8(note);
      out.u8(index + 1);
    }
    // volume, pan and pitch envelopes, all off: flags, node count, loop and sustain points, 25 nodes, a spare byte
    for (let envelope = 0; envelope < 3; envelope++) out.zeros(82);
    out.zeros(INSTRUMENT_SIZE - (out.length - (insAt + index * INSTRUMENT_SIZE)));
  });

  song.sounds.forEach((sound, index) => {
    const final = finals[index];
    out.text("IMPS", 4);
    out.text(dosName(sound.name), 12);
    out.u8(0);
    out.u8(64);
    // flags: has data, 8 bit, mono, loop when set. bit 3 (compressed) is never set
    out.u8(final.data.length ? 1 | (final.loop ? 16 : 0) : 0);
    out.u8(Math.min(64, sound.volume));
    out.text(safeName(sound.name), IT_NAME);
    // signed samples
    out.u8(1);
    out.u8(32);
    out.u32(final.data.length);
    out.u32(final.loopStart);
    out.u32(final.loopEnd);
    out.u32(Math.min(MAX_PLAY_RATE, Math.max(1, Math.round(sound.rate))));
    out.u32(0);
    out.u32(0);
    out.u32(dataAt[index]);
    out.u32(0);
  });

  patterns.forEach((bytes, index) => {
    out.u16(bytes.length);
    out.u16(song.patterns[index].rows);
    out.u32(0);
    out.raw(bytes);
  });
  finals.forEach((sound) => out.raw(sound.data));

  const bytes = out.out();
  return {
    bytes,
    report: { channels, samples: insCount, instruments: insCount, patterns: patCount, sampleBytes: finals.reduce((sum, sound) => sum + sound.data.length, 0), loweredNotes }
  };
}
